import { useApi, useWorkspace } from '~/contexts';
import { useField, useIsMounted } from '~/hooks';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getColorHex } from './taskTypeColors';
import AutoComplete from './AutoComplete';
import FieldControl from './FieldControl';
import Icon from './Icon';

const Option = styled.span`
  display: flex;
  align-items: center;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  margin-right: 0.5rem;
  flex-shrink: 0;
`;

function ProjectTaskTypeSelect({ value, initialValue, onDefault, ...props }) {
  const api = useApi();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [defaultOptions, setDefaultOptions] = useState([]);
  const onDefaultRef = useRef(onDefault);

  const handleSearch = useCallback(
    async (q) => {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projectTaskTypes()
        .get({ q, isActive: true, include: initialValue ? initialValue.id : undefined });

      return data;
    },
    [workspace.id, initialValue, api],
  );

  const refreshDefaultOptions = useCallback(async () => {
    const data = await handleSearch('');
    if (!isMounted.current) return;
    setDefaultOptions(data);
  }, [handleSearch, isMounted]);

  useEffect(() => {
    refreshDefaultOptions();
  }, [refreshDefaultOptions]);

  useEffect(() => {
    onDefaultRef.current = onDefault;
  });

  useEffect(() => {
    if (onDefaultRef.current && defaultOptions.length > 0) {
      const defaultOption = defaultOptions.find((opt) => opt.isDefault);
      if (defaultOption) {
        onDefaultRef.current(defaultOption);
      }
    }
  }, [defaultOptions]);

  const renderOption = (option) => (
    <Option>
      <IconWrapper>
        <Icon icon={option.icon} color={getColorHex(option.color)} />
      </IconWrapper>
      {option.name}
    </Option>
  );

  return (
    <AutoComplete
      onSearch={handleSearch}
      value={value}
      displayText={value ? value.name : ''}
      defaultOptions={defaultOptions}
      renderOption={renderOption}
      {...props}>
      {() => <AutoComplete.Results />}
    </AutoComplete>
  );
}

function FieldProjectTaskTypeSelect(props) {
  const [field, meta] = useField(props);
  const error = meta.touched && meta.error;

  return (
    <FieldControl error={error}>
      <ProjectTaskTypeSelect {...field} {...props} />
    </FieldControl>
  );
}

export default ProjectTaskTypeSelect;
export { FieldProjectTaskTypeSelect };
